import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  MenuItem,
  Chip,
  Grid,
  Divider,
  CircularProgress,
  Autocomplete,
  Avatar,
  Select,
} from "@mui/material";
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { ticketApis } from "../../Data/axios";
import config from "../../Data/AuthUser";

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  "& .MuiDrawer-paper": {
    width: 500,
    padding: theme.spacing(3),
    backgroundColor: "#ffffff",
    color: "#000000",
    "& .MuiTypography-root": {
      color: "#000000",
    },
    "& .MuiTextField-root": {
      "& .MuiInputLabel-root": {
        color: "#000000",
      },
      "& .MuiInputLabel-root.Mui-focused": {
        color: "#1976d2",
      },
      "& .MuiOutlinedInput-root": {
        color: "#000000",
        "& fieldset": {
          borderColor: "#000000",
        },
        "&:hover fieldset": {
          borderColor: "#1976d2",
        },
        "&.Mui-focused fieldset": {
          borderColor: "#1976d2",
        },
      },
      "& .MuiInputBase-input": {
        color: "#000000",
      },
      "& .MuiFormHelperText-root": {
        color: "#d32f2f",
      },
    },
    "& .MuiFormControl-root": {
      "& .MuiInputLabel-root": {
        color: "#000000",
      },
      "& .MuiInputLabel-root.Mui-focused": {
        color: "#1976d2",
      },
      "& .MuiOutlinedInput-root": {
        color: "#000000",
        "& fieldset": {
          borderColor: "#000000",
        },
        "&:hover fieldset": {
          borderColor: "#1976d2",
        },
        "&.Mui-focused fieldset": {
          borderColor: "#1976d2",
        },
      },
      "& .MuiSelect-select": {
        color: "#000000",
      },
      "& .MuiFormHelperText-root": {
        color: "#d32f2f",
      },
    },
    "& .MuiAutocomplete-root": {
      "& .MuiInputLabel-root": {
        color: "#000000",
      },
      "& .MuiInputLabel-root.Mui-focused": {
        color: "#1976d2",
      },
      "& .MuiOutlinedInput-root": {
        color: "#000000",
        "& fieldset": {
          borderColor: "#000000",
        },
        "&:hover fieldset": {
          borderColor: "#1976d2",
        },
        "&.Mui-focused fieldset": {
          borderColor: "#1976d2",
        },
      },
      "& .MuiAutocomplete-option": {
        backgroundColor: "#ffffff",
        color: "#000000",
        "&[aria-selected='true']": {
          backgroundColor: "#e3f2fd",
        },
        "&:hover": {
          backgroundColor: "#f5f5f5",
        },
      },
    },
    "& .MuiDivider-root": {
      borderColor: "#e0e0e0",
    },
    "& .MuiChip-root": {
      backgroundColor: "#f5f5f5",
      color: "#000000",
      borderColor: "#e0e0e0",
    },
    "& .MuiButton-outlined": {
      color: "#000000",
      borderColor: "#000000",
      "&:hover": {
        borderColor: "#1976d2",
        backgroundColor: "#f5f5f5",
      },
    },
  },
}));

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const CreateTicketDrawer = ({
  open,
  onClose,
  onTicketCreated,
  onTicketUpdated,
  showAlert,
  mode = "create",
  ticketData = null
}) => {
  const isUpdateMode = mode === "update" && ticketData;
  const [ticketId, setTicketId] = useState(null);

  // Reverse status label to ID mapping
  const STATUS_LABEL_TO_ID = { "Open": "1", "Assigned": "2", "Resolved": "3", "Closed": "4" };

  // Function to map API ticket data to form structure
  const mapTicketDataToForm = (ticket) => {
    if (!ticket) return {};

    console.log('Mapping ticket data to form:', ticket);

    // Status: handle both raw IDs ("1") and labels ("Open")
    const rawStatus = ticket.status;
    const mappedStatus = STATUS_LABEL_TO_ID[rawStatus] || rawStatus || "1";

    return {
      requester: ticket.user_type ? String(ticket.user_type) : "2",
      requester_id: ticket.requester?.id || ticket.requester_id || "",
      // assing_group: prefer raw ID, fallback to name
      assing_group: ticket.assign_group_id ?? ticket.assing_group ?? ticket.assign_group ?? "",
      // agent: prefer nested object id, then raw agent_id, then raw agent value, then name
      agent: ticket.agent?.id ?? ticket.agent_id ?? ticket.agent ?? ticket.agent_name ?? "",
      user_type: ticket.user_type ? String(ticket.user_type) : "2",
      description: ticket.description || "",
      subject: ticket.subject || "",
      status: String(mappedStatus),
      ticket_priority_id: ticket.priority ? String(ticket.priority) : ticket.ticket_priority_id ? String(ticket.ticket_priority_id) : "1",
      tags: ticket.tags ? (Array.isArray(ticket.tags) ? ticket.tags : ticket.tags.split(',').filter(t => t.trim())) : [],
      // ticket_channel: prefer raw channel_id, then raw ticket_channel, then name
      ticket_channel: ticket.channel_id ? String(ticket.channel_id) : ticket.ticket_channel ? String(ticket.ticket_channel) : (typeof ticket.channel === 'string' ? "" : ticket.channel ? String(ticket.channel) : ""),
      // ticket_type: prefer raw type_id, then raw ticket_type, then name string
      ticket_type: ticket.type_id ?? ticket.ticket_type_id ?? ticket.ticket_type ?? (typeof ticket.type === 'string' ? "" : ticket.type ?? ""),
      ticket_creator: ticket.creator?.id || ticket.ticket_creator || "",
      file: null, // File handling is separate
    };
  };

  // Form state
  const [formData, setFormData] = useState({
    requester: "2",
    requester_id: "",
    assing_group: "",
    agent: "",
    user_type: "2",
    description: "",
    subject: "",
    status: "1",
    ticket_priority_id: "1",
    tags: [],
    ticket_channel: "",
    ticket_type: "",
    ticket_creator: "",
  });

  // Dropdown options state
  const [dropdownData, setDropdownData] = useState({
    users: [],
    ticketTypes: [],
    ticketAgents: [],
    ticketGroups: [],
    ticketChannels: [],
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [uploadedFile, setUploadedFile] = useState(null);
  const [tagInput, setTagInput] = useState("");
  const [dataLoading, setDataLoading] = useState(false);
  const [agentsLoading, setAgentsLoading] = useState(false);

  // Ensure arrays are always valid (defensive programming) - use useMemo to update when dropdownData changes
  const safeDropdownData = useMemo(() => {
    const data = {
      users: Array.isArray(dropdownData.users) ? dropdownData.users : [],
      ticketTypes: Array.isArray(dropdownData.ticketTypes) ? dropdownData.ticketTypes : [],
      ticketAgents: Array.isArray(dropdownData.ticketAgents) ? dropdownData.ticketAgents : [],
      ticketGroups: Array.isArray(dropdownData.ticketGroups) ? dropdownData.ticketGroups : [],
      ticketChannels: Array.isArray(dropdownData.ticketChannels) ? dropdownData.ticketChannels : [],
    };
    console.log("safeDropdownData updated:", {
      users: data.users.length,
      ticketTypes: data.ticketTypes.length,
      ticketAgents: data.ticketAgents.length,
      ticketGroups: data.ticketGroups.length,
      ticketChannels: data.ticketChannels.length,
    });
    return data;
  }, [dropdownData]);

  // Constants
  const requesterTypes = [
    { value: "1", label: "Client" },
    { value: "2", label: "Employee" },
    { value: "3", label: "App User" },
  ];

  const userTypes = [
    { value: "1", label: "Driver" },
    { value: "2", label: "Owner" },
    { value: "3", label: "Dhaba" },
    { value: "4", label: "Mechanic" },
    { value: "5", label: "Crane" },
    { value: "6", label: "Operator" },
  ];

  const priorityTypes = [
    { value: "1", label: "Low" },
    { value: "2", label: "Medium" },
    { value: "3", label: "High" },
    { value: "4", label: "Urgent" },
  ];

  const statusTypes = [
    { value: "1", label: "Open" },
    { value: "0", label: "Closed" },
    { value: "2", label: "Assigned" },
    { value: "3", label: "Resolved" },
  ];

  // Get initials from name/username
  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Get user display name based on requester type
  const getUserDisplayName = (user) => {
    const requesterType = formData.requester;
    if (requesterType === "3") {
      return user.name || "";
    }
    return user.username || "";
  };

  // Get user ID field based on requester type
  const getUserIdValue = (user) => {
    const requesterType = formData.requester;
    // 1->Client, 2->Employee, 3->App User
    // Client uses user_id, Employee uses empid, App User uses empid/name
    if (requesterType === "2" || requesterType === "3") {
      return user.empid || user.user_id || "";
    }
    return user.user_id || "";
  };

  // Get search fields for filtering users
  const getUserSearchString = (user) => {
    const requesterType = formData.requester;
    if (requesterType === "3") {
      return `${user.name} ${user.empid} ${user.email}`.toLowerCase();
    }
    return `${user.username} ${user.user_id} ${user.phone}`.toLowerCase();
  };

  // Fetch prerequisite data based on requester type
  const fetchPrerequisiteData = useCallback(async () => {
    setDataLoading(true);
    try {
      const response = await ticketApis.getprerequisiteDataForTicketCreation(
        formData.requester
      );

      console.log("Prerequisite Data API Response:", response);
      console.log("Full response:", JSON.stringify(response, null, 2));

      if (response.success && response.data) {
        // API returns data wrapped in an object: { data: { users: [...], ticket_types: [...], ... } }
        const responseData = response.data.data || {};

        console.log("Response data.users:", responseData.users);
        console.log("Response data.ticket_types:", responseData.ticket_types);
        console.log("Response data.ticket_groups:", responseData.ticket_groups);
        console.log("Response data.ticket_channels:", responseData.ticket_channels);

        // Preserve existing ticketAgents, update other arrays
        setDropdownData((prev) => {
          const newData = {
            ...prev,
            users: responseData.users || [],
            ticketTypes: responseData.ticket_types || [],
            ticketGroups: responseData.ticket_groups || [],
            ticketChannels: responseData.ticket_channels || [],
          };
          console.log("Setting dropdownData:", newData);
          return newData;
        });
      } else {
        setDropdownData({
          users: [],
          ticketTypes: [],
          ticketAgents: [],
          ticketGroups: [],
          ticketChannels: [],
        });
      }
    } catch (error) {
      console.error("Error fetching prerequisite data:", error);
      showAlert?.("Failed to load form data", "error");
      setDropdownData({
        users: [],
        ticketTypes: [],
        ticketAgents: [],
        ticketGroups: [],
        ticketChannels: [],
      });
    } finally {
      setDataLoading(false);
    }
  }, [formData.requester, showAlert]);

  // Fetch ticket agents by group ID
  const fetchAgentsByGroup = useCallback(async (groupId) => {
    if (!groupId) {
      setDropdownData((prev) => ({ ...prev, ticketAgents: [] }));
      return;
    }

    setAgentsLoading(true);
    try {
      const response = await ticketApis.getAgentsByGroupId(groupId);
      if (response.success) {
        const agents = Array.isArray(response.data) ? response.data : [];
        setDropdownData((prev) => ({ ...prev, ticketAgents: agents }));
      } else {
        setDropdownData((prev) => ({ ...prev, ticketAgents: [] }));
      }
    } catch (error) {
      console.error("Error fetching agents by group:", error);
      setDropdownData((prev) => ({ ...prev, ticketAgents: [] }));
    } finally {
      setAgentsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      // Set ticket creator from localStorage
      const creatorId = localStorage.getItem("empid");
      if (creatorId) {
        setFormData((prev) => ({ ...prev, ticket_creator: creatorId }));
      }
      // Fetch prerequisite data only
      fetchPrerequisiteData();

      // If in update mode, load ticket data
      if (isUpdateMode && ticketData) {
        setTicketId(ticketData.ticket_id || ticketData.id);

        const mappedData = mapTicketDataToForm(ticketData);

        console.log('Mapped ticket data:', mappedData);

        setFormData((prev) => ({
          ...prev,
          ...mappedData,
        }));

        // Fetch agents for the assigned group if it exists
        if (mappedData.assing_group) {
          fetchAgentsByGroup(mappedData.assing_group);
        }
      }
    }
  }, [open, fetchPrerequisiteData, isUpdateMode, ticketData, fetchAgentsByGroup]);

  const handleInputChange = (field, value) => {
    // Prevent editing non-editable fields in update mode
    const nonEditableFields = ['subject', 'requester', 'requester_id', 'user_type'];
    if (isUpdateMode && nonEditableFields.includes(field)) {
      return;
    }

    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }

    // If requester type changed, clear requester_id and refetch data
    if (field === "requester") {
      setFormData((prev) => ({ ...prev, requester_id: "" }));
    }

    // If group changed, fetch agents for that group and clear agent selection
    if (field === "assing_group") {
      setFormData((prev) => ({ ...prev, agent: "" }));
      fetchAgentsByGroup(value);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        showAlert?.("File size should be less than 10MB", "error");
        return;
      }
      setUploadedFile(file);
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Only validate these fields in create mode
    if (!isUpdateMode) {
      if (!formData.subject.trim()) newErrors.subject = "Subject is required";
      if (!formData.requester_id.trim())
        newErrors.requester_id = "Requester ID is required";
    }

    // Validate editable fields (these are always required)
    if (!formData.assing_group) newErrors.assing_group = "Group is required";
    if (!formData.agent) newErrors.agent = "Agent is required";
    if (!formData.ticket_channel)
      newErrors.ticket_channel = "Channel is required";
    if (!formData.ticket_type) newErrors.ticket_type = "Type is required";

    // Description is always required for both create and update
    if (!formData.description.trim())
      newErrors.description = "Description is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      showAlert?.("Please fill in all required fields", "error");
      return;
    }

    setLoading(true);
    try {
      const submitData = new FormData();

      // Fields that need to be sent as integers for Laravel validation
      const integerFields = ['user_type', 'status', 'ticket_priority_id', 'ticket_channel', 'ticket_type'];
      // requester_id should remain as string (varchar in database)

      // In update mode, only send editable fields
      const editableFields = ['assing_group', 'agent', 'description', 'status', 'file',
                             'ticket_priority_id', 'ticket_channel', 'ticket_type'];

      Object.keys(formData).forEach((key) => {
        const value = formData[key];

        // Skip ticket_creator as it's set from localStorage and handled separately
        if (key === 'ticket_creator') {
          submitData.append(key, value);
          return;
        }

        // In update mode, only include editable fields and file
        if (isUpdateMode && !editableFields.includes(key) && key !== 'tags') {
          return;
        }

        if (key === "tags") {
          // Convert tags array to comma-separated string
          submitData.append(key, Array.isArray(value) ? value.join(',') : '');
        } else if (key === "requester_id") {
          // requester_id should be sent as string (varchar in DB)
          submitData.append(key, value || '');
        } else if (integerFields.includes(key)) {
          // Convert to integer for Laravel validation
          submitData.append(key, parseInt(value, 10) || 0);
        } else {
          // For other fields (strings), send as-is
          submitData.append(key, value || '');
        }
      });

      // Append file if uploaded
      if (uploadedFile) {
        submitData.append("file", uploadedFile);
      }

      let response;
      if (isUpdateMode && ticketId) {
        // Update existing ticket
        response = await ticketApis.updateTicket(ticketId, submitData);
        if (response.success) {
          showAlert?.("Ticket updated successfully", "success");
          onTicketUpdated?.();
          handleClose();
        } else {
          showAlert?.(response.message || "Failed to update ticket", "error");
        }
      } else {
        // Create new ticket
        response = await ticketApis.createTicket(submitData);
        if (response.success) {
          showAlert?.("Ticket created successfully", "success");
          onTicketCreated?.();
          handleClose();
        } else {
          showAlert?.(response.message || "Failed to create ticket", "error");
        }
      }
    } catch (error) {
      console.error("Error saving ticket:", error);
      showAlert?.(isUpdateMode ? "Failed to update ticket" : "Failed to create ticket", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      requester: "2",
      requester_id: "",
      assing_group: "",
      agent: "",
      user_type: "2",
      description: "",
      subject: "",
      status: "1",
      ticket_priority_id: "1",
      tags: [],
      ticket_channel: "",
      ticket_type: "",
      ticket_creator: localStorage.getItem("empid") || "",
    });
    setUploadedFile(null);
    setTagInput("");
    setErrors({});
    onClose();
  };

  return (
    <StyledDrawer anchor="right" open={open} onClose={handleClose}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5" fontWeight="bold" sx={{ color: "#000000" }}>
          {isUpdateMode ? "Update Ticket" : "Create New Ticket"}
        </Typography>
        <IconButton onClick={handleClose} sx={{ color: "#000000" }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider sx={{ mb: 3, borderColor: "#e0e0e0" }} />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {isUpdateMode ? (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: "#000000" }}>
                Subject (Read Only)
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  p: 2,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 1,
                  color: "#333333",
                  border: "1px solid #e0e0e0"
                }}
              >
                {formData.subject || "N/A"}
              </Typography>
            </Box>
          ) : (
            <TextField
              label="Subject *"
              fullWidth
              value={formData.subject}
              onChange={(e) => handleInputChange("subject", e.target.value)}
              error={!!errors.subject}
              helperText={errors.subject}
              disabled={isUpdateMode}
              sx={isUpdateMode ? {
                '& .MuiInputBase-root.Mui-disabled': {
                  backgroundColor: '#f5f5f5',
                }
              } : {}}
            />
          )}

          <TextField
            label="Description *"
            fullWidth
            multiline
            rows={4}
            value={formData.description}
            onChange={(e) => handleInputChange("description", e.target.value)}
            error={!!errors.description}
            helperText={errors.description}
          />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              {isUpdateMode ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: "#000000" }}>
                    Requester Type (Read Only)
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      p: 2,
                      backgroundColor: "#f5f5f5",
                      borderRadius: 1,
                      color: "#333333",
                      border: "1px solid #e0e0e0"
                    }}
                  >
                    {requesterTypes.find(t => t.value === formData.requester)?.label || "N/A"}
                  </Typography>
                </Box>
              ) : (
                <FormControl fullWidth>
                  <InputLabel>Requester Type</InputLabel>
                  <Select
                    value={formData.requester}
                    onChange={(e) => handleInputChange("requester", e.target.value)}
                    label="Requester Type"
                  >
                    {requesterTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>

            <Grid item xs={6}>
              {isUpdateMode ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: "#000000" }}>
                    Requester ID (Read Only)
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      p: 2,
                      backgroundColor: "#f5f5f5",
                      borderRadius: 1,
                      color: "#333333",
                      border: "1px solid #e0e0e0"
                    }}
                  >
                    {formData.requester_id || "N/A"}
                  </Typography>
                </Box>
              ) : (
                <Autocomplete
                  fullWidth
                  loading={dataLoading}
                  loadingText="Loading..."
                  disabled={dataLoading && safeDropdownData.users.length === 0}
                  options={safeDropdownData.users}
                  getOptionLabel={(user) => getUserDisplayName(user) || ""}
                  value={
                    safeDropdownData.users.find(
                      (u) => getUserIdValue(u) === formData.requester_id
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    handleInputChange(
                      "requester_id",
                      newValue ? getUserIdValue(newValue) : ""
                    );
                  }}
                  filterOptions={(options, { inputValue }) => {
                    const searchLower = inputValue.toLowerCase();
                    return options.filter((user) =>
                      getUserSearchString(user).includes(searchLower)
                    );
                  }}
                  isOptionEqualToValue={(option, value) =>
                    getUserIdValue(option) === getUserIdValue(value)
                  }
                  renderOption={(props, option) => (
                    <li
                      {...props}
                      style={{ backgroundColor: "#ffffff", color: "#000000" }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          width: "100%",
                          backgroundColor: "#ffffff",
                          color: "#000000",
                        }}
                      >
                        {option.image ? (
                          <Avatar
                            src={`${config.apiImageUrl}${option.image}`}
                            alt={getUserDisplayName(option)}
                            sx={{ width: 32, height: 32 }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.style.display = "none";
                            }}
                          />
                        ) : (
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              backgroundColor: "#1976d2",
                              color: "#ffffff",
                            }}
                          >
                            {getInitials(getUserDisplayName(option))}
                          </Avatar>
                        )}
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Typography variant="body2" sx={{ color: "#000000" }}>
                            {getUserDisplayName(option)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#666666" }}>
                            ID: {getUserIdValue(option)}
                          </Typography>
                        </Box>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Requester ID *"
                      error={!!errors.requester_id}
                      helperText={errors.requester_id}
                      placeholder="Search by name, phone, or ID"
                    />
                  )}
                />
              )}
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              {isUpdateMode ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: "#000000" }}>
                    User Type (Read Only)
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      p: 2,
                      backgroundColor: "#f5f5f5",
                      borderRadius: 1,
                      color: "#333333",
                      border: "1px solid #e0e0e0"
                    }}
                  >
                    {userTypes.find(t => t.value === formData.user_type)?.label || "N/A"}
                  </Typography>
                </Box>
              ) : (
                <FormControl fullWidth>
                  <InputLabel>User Type</InputLabel>
                  <Select
                    value={formData.user_type}
                    onChange={(e) =>
                      handleInputChange("user_type", e.target.value)
                    }
                    label="User Type"
                  >
                    {userTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.ticket_priority_id}
                  onChange={(e) =>
                    handleInputChange("ticket_priority_id", e.target.value)
                  }
                  label="Priority"
                >
                  {priorityTypes.map((priority) => (
                    <MenuItem key={priority.value} value={priority.value}>
                      {priority.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Autocomplete
            fullWidth
            loading={dataLoading}
            loadingText="Loading..."
            disabled={dataLoading && safeDropdownData.ticketGroups.length === 0}
            options={safeDropdownData.ticketGroups}
            noOptionsText="No options"
            getOptionLabel={(group) =>
              group.ticket_group || group.group_name || ""
            }
            value={
              safeDropdownData.ticketGroups.find(
                (g) => String(g.id) === String(formData.assing_group)
              ) || null
            }
            onChange={(event, newValue) => {
              handleInputChange("assing_group", newValue ? newValue.id : "");
            }}
            filterOptions={(options, { inputValue }) => {
              const searchLower = inputValue.toLowerCase();
              return options.filter(
                (group) =>
                  (group.ticket_group || group.group_name || "")
                    .toLowerCase()
                    .includes(searchLower)
              );
            }}
            isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Assign Group *"
                error={!!errors.assing_group}
                helperText={errors.assing_group}
                placeholder="Search group..."
              />
            )}
          />

          <Autocomplete
            fullWidth
            loading={agentsLoading}
            loadingText="Loading agents..."
            disabled={agentsLoading || !formData.assing_group}
            options={safeDropdownData.ticketAgents}
            noOptionsText={!formData.assing_group ? "Select a group first" : "No agents found"}
            getOptionLabel={(agent) =>
                agent.user || agent.name || agent.agent_name || agent.username || agent.agent_id || ""
              }
              value={
                safeDropdownData.ticketAgents.find(
                  (a) => a.agent_id === formData.agent
                ) || null
              }
              onChange={(event, newValue) => {
                handleInputChange("agent", newValue ? newValue.agent_id : "");
              }}
              filterOptions={(options, { inputValue }) => {
                const searchLower = inputValue.toLowerCase();
                return options.filter(
                  (agent) =>
                    (
                      agent.user ||
                      agent.name ||
                      agent.agent_name ||
                      agent.username ||
                      agent.agent_id ||
                      agent.email ||
                      agent.mobile ||
                      ""
                    )
                      .toLowerCase()
                      .includes(searchLower)
                );
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderOption={(props, option) => {
                const agentName = option.user || option.name || option.agent_name || option.agent_id || "Unknown";
                const imageUrl = option.requester_Image
                  ? `${config.apiImageUrl}${option.requester_Image}`
                  : null;

                return (
                  <li
                    {...props}
                    style={{
                      backgroundColor: "#ffffff",
                      color: "#000000",
                      padding: "8px 12px",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        width: "100%",
                      }}
                    >
                      {imageUrl ? (
                        <Avatar
                          src={imageUrl}
                          alt={agentName}
                          sx={{ width: 36, height: 36 }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = "none";
                            e.target.parentElement.style.display = "none";
                          }}
                        />
                      ) : (
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            backgroundColor: "#1976d2",
                            color: "#ffffff",
                            fontSize: "14px",
                          }}
                        >
                          {getInitials(agentName)}
                        </Avatar>
                      )}
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Typography
                          variant="body2"
                          sx={{ color: "#000000", fontWeight: 500 }}
                        >
                          {agentName}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "#666666", fontSize: "12px" }}
                        >
                          ID: {option.agent_id || "N/A"} {option.email ? `| ${option.email}` : ""} {option.mobile ? `| ${option.mobile}` : ""}
                        </Typography>
                      </Box>
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Assign Agent *"
                  error={!!errors.agent}
                  helperText={errors.agent}
                  placeholder={!formData.assing_group ? "Select a group first" : "Search agent..."}
                />
              )}
            />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Autocomplete
                fullWidth
                loading={dataLoading}
                loadingText="Loading..."
                disabled={dataLoading && safeDropdownData.ticketChannels.length === 0}
                options={safeDropdownData.ticketChannels}
                noOptionsText="No options"
                getOptionLabel={(channel) =>
                  channel.channel || channel.channel_name || ""
                }
                value={
                  safeDropdownData.ticketChannels.find(
                    (c) => String(c.id) === String(formData.ticket_channel)
                  ) || null
                }
                onChange={(event, newValue) => {
                  handleInputChange("ticket_channel", newValue ? newValue.id : "");
                }}
                filterOptions={(options, { inputValue }) => {
                  const searchLower = inputValue.toLowerCase();
                  return options.filter(
                    (channel) =>
                      (
                        channel.channel || channel.channel_name || ""
                      )
                        .toLowerCase()
                        .includes(searchLower)
                  );
                }}
                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Channel *"
                    error={!!errors.ticket_channel}
                    helperText={errors.ticket_channel}
                    placeholder="Search channel..."
                  />
                )}
              />
            </Grid>

            <Grid item xs={6}>
              <Autocomplete
                fullWidth
                loading={dataLoading}
                loadingText="Loading..."
                disabled={dataLoading && safeDropdownData.ticketTypes.length === 0}
                options={safeDropdownData.ticketTypes}
                noOptionsText="No options"
                getOptionLabel={(type) => type.type || type.type_name || ""}
                value={
                  safeDropdownData.ticketTypes.find(
                    (t) => String(t.id) === String(formData.ticket_type)
                  ) || null
                }
                onChange={(event, newValue) => {
                  handleInputChange("ticket_type", newValue ? newValue.id : "");
                }}
                filterOptions={(options, { inputValue }) => {
                  const searchLower = inputValue.toLowerCase();
                  return options.filter(
                    (type) =>
                      (type.type || type.type_name || "")
                        .toLowerCase()
                        .includes(searchLower)
                  );
                }}
                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Type *"
                    error={!!errors.ticket_type}
                    helperText={errors.ticket_type}
                    placeholder="Search type..."
                  />
                )}
              />
            </Grid>
          </Grid>

          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status}
              onChange={(e) => handleInputChange("status", e.target.value)}
              label="Status"
            >
              {statusTypes.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                  {status.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, color: "#000000" }}>
              Tags
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
              <TextField
                size="small"
                placeholder="Add tag"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleAddTag()}
                sx={{ flexGrow: 1 }}
              />
              <Button
                variant="outlined"
                onClick={handleAddTag}
                sx={{
                  color: "#000000",
                  borderColor: "#000000",
                  "&:hover": {
                    borderColor: "#1976d2",
                    backgroundColor: "#f5f5f5",
                  },
                }}
              >
                Add
              </Button>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {formData.tags.map((tag, index) => (
                <Chip
                  key={index}
                  label={tag}
                  onDelete={() => handleRemoveTag(tag)}
                  size="small"
                  sx={{
                    backgroundColor: "#f5f5f5",
                    color: "#000000",
                    border: "1px solid #e0e0e0",
                  }}
                />
              ))}
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, color: "#000000" }}>
              Attachment
            </Typography>
            <Button
              component="label"
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              fullWidth
              sx={{
                color: "#000000",
                borderColor: "#000000",
                "&:hover": {
                  borderColor: "#1976d2",
                  backgroundColor: "#f5f5f5",
                },
              }}
            >
              Upload File
              <VisuallyHiddenInput
                type="file"
                onChange={handleFileUpload}
                accept="image/*,.pdf,.doc,.docx,.txt"
              />
            </Button>
            {uploadedFile && (
              <Typography variant="body2" sx={{ mt: 1, color: "#666666" }}>
                Selected: {uploadedFile.name}
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 2, borderColor: "#e0e0e0" }} />

          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={loading}
              sx={{
                color: "#000000",
                borderColor: "#000000",
                "&:hover": {
                  borderColor: "#1976d2",
                  backgroundColor: "#f5f5f5",
                },
                "&:disabled": {
                  color: "#999999",
                  borderColor: "#cccccc",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              startIcon={
                loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <SendIcon />
                )
              }
              disabled={loading}
              sx={{
                backgroundColor: "#1976d2",
                color: "#ffffff",
                "&:hover": {
                  backgroundColor: "#1565c0",
                },
                "&:disabled": {
                  backgroundColor: "#cccccc",
                  color: "#666666",
                },
              }}
            >
              {loading ? (isUpdateMode ? "Updating..." : "Creating...") : (isUpdateMode ? "Update Ticket" : "Create Ticket")}
            </Button>
          </Box>
        </Box>
    </StyledDrawer>
  );
};

export default CreateTicketDrawer;
